import { useEffect, useState } from 'react';
import { KeyIcon } from './icons';

const MAX_DIGITS = 8;
const MIN_DIGITS = 4;
const IDLE_DISMISS_MS = 60_000;

interface PinDialogProps {
  open: boolean;
  title: string;
  /** Resolves when the PIN is accepted; rejects with a message to show (e.g. "Wrong PIN"). */
  onSubmit: (pin: string) => Promise<void>;
  onCancel: () => void;
}

/** On-screen keypad for the owner's kiosk PIN (touch kiosks usually have no keyboard). */
export function PinDialog({ open, title, onSubmit, onCancel }: PinDialogProps) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin('');
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(onCancel, IDLE_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [open, onCancel, pin]);

  const press = (d: string) => {
    if (busy) return;
    setError(null);
    setPin((p) => (p.length < MAX_DIGITS ? p + d : p));
  };
  const submit = async () => {
    if (busy || pin.length < MIN_DIGITS) return;
    setBusy(true);
    try {
      await onSubmit(pin);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wrong PIN');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const key = 'grid h-[9vmin] place-items-center rounded-[1.6vmin] text-[4vmin] font-bold ring-1 ring-line active:bg-white/15';
  return (
    <div
      className={`fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
        open ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      aria-hidden={!open}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-title"
        className="w-[min(64vmin,90vw)] rounded-[3vmin] bg-ink-2 p-[4vmin] ring-2 ring-line"
      >
        <div className="flex items-center gap-4">
          <KeyIcon className="size-[5vmin] text-accent" />
          <h2 id="pin-title" className="text-[3.2vmin] font-bold">
            {title}
          </h2>
        </div>
        <p className="mt-2 text-[2.1vmin] text-muted">Enter the kiosk PIN set by the owner.</p>
        <div className="mt-[3vmin] flex h-[5vmin] items-center justify-center gap-[1.6vmin]" aria-live="polite">
          {pin.length === 0 ? (
            <span className={`text-[2.2vmin] ${error ? 'font-semibold text-bad' : 'text-muted'}`}>{error ?? '4–8 digits'}</span>
          ) : (
            Array.from({ length: pin.length }, (_, i) => <span key={i} className="size-[2.4vmin] rounded-full bg-text" />)
          )}
        </div>
        <div className="mt-[3vmin] grid grid-cols-3 gap-[1.6vmin]">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" className={key} onClick={() => press(d)}>
              {d}
            </button>
          ))}
          <button type="button" className={`${key} text-[2.6vmin]`} onClick={() => setPin((p) => p.slice(0, -1))}>
            ⌫
          </button>
          <button type="button" className={key} onClick={() => press('0')}>
            0
          </button>
          <button
            type="button"
            className={`${key} bg-accent text-[2.8vmin] text-accent-ink ring-accent disabled:opacity-40`}
            onClick={() => void submit()}
            disabled={busy || pin.length < MIN_DIGITS}
          >
            {busy ? '…' : 'OK'}
          </button>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="mt-[2.4vmin] w-full rounded-[1.6vmin] py-[1.6vmin] text-[2.3vmin] font-semibold text-muted active:bg-white/10"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
